"use client";

import { useChains } from "@opensea/app-kit";
import { classNames, useToasts } from "@opensea/ui-kit";
import { bn } from "@opensea/utils";
import { head } from "es-toolkit/array";
import { useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";
import { ActionTimeline } from "../../../../components/ActionTimeline";
import { Button } from "../../../../components/Button";
import { useAddress, useChainArch, useConnect, useConnections, useIsReady } from "../../../../polychain/hooks";
import { useNavigation } from "../../../../providers/NavigationProvider";
import { useTrackError } from "../../../../providers/TrackErrorProvider";
import { FlowActions } from "../../../../stores/flowStore";
import { useConnectFlow } from "../../../hooks/useConnectFlow";
import { useSwapButtonText } from "../hooks/use-swap-button-text";
import { useTrackSwapEvent } from "../hooks/use-track-swap-event";
import { useSwapGasCheck } from "../hooks/useSwapGasCheck";
import { readSwapImpact } from "../provider/readSwapImpact";
import { useSwapContext } from "../provider/SwapContext";
import { MAX_SLIPPAGE_WARNING_THRESHOLD, SWAP_IMPACT_WARNING_THRESHOLD } from "./constants";
export const SwapActions = ({
  className,
  onCompleted,
  onTransactionStart,
  buttonColor,
  surface = "inline"
}) => {
  const t = useTranslations("wallet.SwapActions");
  const {
    actions,
    quote,
    isLoadingQuote,
    waitForCompletion,
    setShouldShowConfirmationModal,
    setActionsStatus
  } = useSwapContext();
  const {
    control,
    formState: {
      isValid: isFormStateValid,
      isSubmitting
    }
  } = useFormContext();
  const trackSwapEvent = useTrackSwapEvent(quote);
  const {
    hasGas
  } = useSwapGasCheck();
  const buttonText = useSwapButtonText();
  const {
    showErrorMessage
  } = useToasts();
  const trackError = useTrackError();
  const {
    renderLink: Link
  } = useNavigation();
  const {
    getChain
  } = useChains();
  const fromCurrency = useWatch({
    control,
    name: "fromCurrency"
  });
  const onActionCompleted = useCallback(async (response, index) => {
    if (response.action === "TRANSACTION") {
      if (index === actions.length - 1) {
        onTransactionStart?.();
      }
      await response.wait();
      if (index === actions.length - 1) {
        FlowActions.setFlowField("transactionIdentifier", {
          chain: response.transactionIdentifier.chain,
          transactionHash: response.transactionIdentifier.hash
        });
        try {
          const completedReceipt = await waitForCompletion(response.transactionIdentifier);
          if (completedReceipt.status === "SUCCESS") {
            FlowActions.onSuccess();
            trackSwapEvent("Swap Succeeded", {
              transaction: {
                hash: response.transactionIdentifier.hash,
                chain: response.transactionIdentifier.chain
              }
            });
          }
          onCompleted(completedReceipt);
        } catch (error) {
          const errorId = trackError(error, {
            extra: {
              transactionIdentifier: response.transactionIdentifier
            }
          });
          trackSwapEvent("Swap Receipt Error", {
            errorId,
            error: error instanceof Error ? error.message : "Unknown error",
            transaction: {
              hash: response.transactionIdentifier.hash,
              chain: response.transactionIdentifier.chain
            }
          });

          // Use getChain to derive explorer URL from actual transaction chain
          // (which may differ from fromCurrency.chainIdentifier in cross-chain swaps)
          const chain = getChain(response.transactionIdentifier.chain);
          const explorerUrl = chain?.blockExplorer.transactionUrlTemplate.replace(":hash", response.transactionIdentifier.hash);
          const explorerName = chain?.blockExplorer.name;
          showErrorMessage(t("failedToGetSwapReceipt"), {
            description: explorerUrl ? <Link href={explorerUrl}>
                  {t("viewOnExplorer", {
                explorerName: explorerName ?? "Explorer"
              })}
                </Link> : undefined
          });
        }
      }
    }
  }, [actions.length, onCompleted, trackSwapEvent, waitForCompletion, onTransactionStart, getChain, showErrorMessage, trackError, t, Link]);
  const connectFlow = useConnectFlow();
  const chainArch = useChainArch();
  const address = useAddress();
  const walletReady = useIsReady();
  const maxSlippage = useWatch({
    control,
    name: "maxSlippage"
  });
  const fromCurrencyChainArch = getChain(fromCurrency.chainIdentifier)?.arch;
  const connections = useConnections();
  const {
    connect
  } = useConnect();
  const isFromSvm = fromCurrencyChainArch === "SVM";
  const isFromEvm = fromCurrencyChainArch === "EVM";
  if (surface === "inline" && isFromSvm && (chainArch !== "SVM" || !address)) {
    const solanaConnection = head(connections.filter(connection => connection.chainArch === "SVM"));
    const onClick = async () => {
      if (solanaConnection) {
        await connect(solanaConnection.connector);
      } else {
        connectFlow({
          chainArch: "SVM"
        });
      }
    };
    return <div className={classNames("p-4", className)}>
        <Button className="w-full" disabled={!walletReady} isLoading={!walletReady} onClick={onClick} style={{
        backgroundColor: buttonColor
      }} type="button">
          {solanaConnection ? t("switchToSolanaWallet") : t("connectSolanaWallet")}
        </Button>
      </div>;
  }
  if (!(address && walletReady)) {
    return <div className={classNames("p-4", className)}>
        <Button className="w-full" disabled={!walletReady} isLoading={!walletReady} onClick={() => connectFlow({
        chainArch: isFromEvm ? "EVM" : undefined
      })} style={{
        backgroundColor: buttonColor
      }} type="button">
          {t("connectWallet")}
        </Button>
      </div>;
  }
  if (!actions.length) {
    const isLoading = isSubmitting || isLoadingQuote;
    const swapRoute = quote?.swapRoutes[0];
    const disabled = isLoading || !isFormStateValid || swapRoute?.__typename !== "SwapRoute" || !hasGas;
    const swapImpact = swapRoute?.__typename === "SwapRoute" ? readSwapImpact(swapRoute) : 0;
    const hasHighImpact = swapImpact && bn(swapImpact.percent).lt(SWAP_IMPACT_WARNING_THRESHOLD);
    const hasHighSlippage = maxSlippage && maxSlippage > MAX_SLIPPAGE_WARNING_THRESHOLD;
    const shouldShowWarning = hasHighImpact || hasHighSlippage;
    return <div className={classNames("p-4", className)}>
        <Button className="w-full" disabled={disabled} isLoading={isLoading} onClick={shouldShowWarning ? () => setShouldShowConfirmationModal(true) : undefined} style={{
        backgroundColor: buttonColor
      }} type={shouldShowWarning ? undefined : "submit"}>
          {buttonText}
        </Button>
      </div>;
  }
  return <div className={classNames("p-4", className)}>
      <ActionTimeline actions={actions} onActionCompleted={onActionCompleted} onStatusChange={setActionsStatus} />
    </div>;
};